/**
 * Welle 118 – DB-Tabellen 2:
 *  • ExcelAbgleich: die echte Daten.xlsm hochladen → jede Lasche, die es als DB-2-Tabelle gibt, wird mit der DB
 *    verglichen (neu · geändert je Zelle · nur in der DB). Übernommen wird nur, was angehakt ist.
 *  • VbaDialog: Live-Modul einer Tabelle herunterladen (Mac/Windows) — in Excel importieren und starten, dann
 *    gleicht die Lasche alle paar Sekunden mit der DB ab (beide Richtungen).
 */
import React, { useMemo, useRef, useState } from "react";

const fmt = (v) => (v === null || v === undefined || v === "" ? "–" : String(v));

export function ExcelAbgleich({ B, onClose, onUebernommen }) {
  const dateiRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState("");
  const [bericht, setBericht] = useState(null);
  const [aktiv, setAktiv] = useState(0);
  const [auswahl, setAuswahl] = useState({});      // {tabelle: {neu:Set, zellen:{pk:Set}, ganz:bool}}
  const [ergebnis, setErgebnis] = useState(null);

  const hochladen = async (datei) => {
    if (!datei) return;
    setBusy(true); setFehler(""); setBericht(null); setErgebnis(null); setAuswahl({});
    try {
      const fd = new FormData();
      fd.append("datei", datei);
      const r = await fetch(`${B}/excel-abgleich`, { method: "POST", body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `Fehler ${r.status}`);
      // Laschen mit Abweichungen zuerst
      d.laschen.sort((a, b) => anzahl(b) - anzahl(a));
      setBericht(d);
      setAktiv(0);
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const anzahl = (l) => (l.fehler ? 1 : (l.neu?.length || 0) + (l.geaendert || []).reduce((s, g) => s + g.zellen.length, 0));

  const sel = (t) => auswahl[t] || { neu: new Set(), zellen: {}, ganz: false };
  const setSel = (t, f) => setAuswahl((a) => ({ ...a, [t]: f(sel(t)) }));
  const zelleUmschalten = (t, pk, spalte) => setSel(t, (s) => {
    const z = { ...s.zellen };
    const set = new Set(z[pk] || []);
    set.has(spalte) ? set.delete(spalte) : set.add(spalte);
    z[pk] = set;
    return { ...s, zellen: z };
  });
  const zeileUmschalten = (t, g, an) => setSel(t, (s) => ({ ...s, zellen: { ...s.zellen, [g.pk]: new Set(an ? g.zellen.map((z) => z.spalte) : []) } }));
  const neuUmschalten = (t, nr) => setSel(t, (s) => {
    const n = new Set(s.neu);
    n.has(nr) ? n.delete(nr) : n.add(nr);
    return { ...s, neu: n };
  });
  const alleGeaendert = (l, an) => setSel(l.tabelle, (s) => ({
    ...s, zellen: Object.fromEntries((l.geaendert || []).map((g) => [g.pk, new Set(an ? g.zellen.map((z) => z.spalte) : [])])) }));
  const alleNeu = (l, an) => setSel(l.tabelle, (s) => ({ ...s, neu: new Set(an ? (l.neu || []).map((n) => n.excel_zeile) : []) }));

  const gewaehlt = useMemo(() => {
    let n = 0;
    Object.values(auswahl).forEach((s) => {
      n += s.neu.size;
      Object.values(s.zellen).forEach((z) => { n += z.size; });
      if (s.ganz) n += 1;
    });
    return n;
  }, [auswahl]);

  const uebernehmen = async () => {
    const senden = {};
    Object.entries(auswahl).forEach(([t, s]) => {
      const zellen = Object.fromEntries(Object.entries(s.zellen).filter(([, set]) => set.size).map(([pk, set]) => [pk, [...set]]));
      if (s.neu.size || Object.keys(zellen).length || s.ganz) senden[t] = { neu: [...s.neu], zellen, ganz: !!s.ganz };
    });
    if (!Object.keys(senden).length) return;
    const text = Object.entries(senden).map(([t, s]) => `• ${t}: ${s.ganz ? "ganze Lasche inkl. Formatierung" :
      `${s.neu.length} neue Zeilen, ${Object.values(s.zellen).reduce((n, z) => n + z.length, 0)} Zellen`}`).join("\n");
    if (!window.confirm(`In die Datenbank übernehmen?\n\n${text}`)) return;
    setBusy(true); setFehler("");
    try {
      const r = await fetch(`${B}/excel-abgleich/${bericht.id}/uebernehmen`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ auswahl: senden }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `Fehler ${r.status}`);
      setErgebnis(d.ergebnis);
      setAuswahl({});
      onUebernommen && onUebernommen();
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const l = bericht?.laschen?.[aktiv];
  const s = l ? sel(l.tabelle) : null;

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="diff-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 1180 }}>
        <div className="diff-modal-header">
          <h2>📊 Daten.xlsm mit der Datenbank abgleichen</h2>
          <button className="excel-button" onClick={onClose}>✕</button>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", margin: "8px 0 12px" }}>
          <input ref={dateiRef} type="file" accept=".xlsm,.xlsx" style={{ display: "none" }}
            onChange={(e) => { hochladen(e.target.files?.[0]); e.target.value = ""; }} />
          <button className="diff-apply-btn" disabled={busy} onClick={() => dateiRef.current?.click()}>
            {busy && !bericht ? "⏳ vergleicht … (ca. 10 s)" : "📂 Daten.xlsm wählen"}</button>
          <span style={{ fontSize: 13, opacity: 0.8 }}>
            Verglichen werden alle Laschen, die es in DB-Tabellen 2 gibt. Es wird nichts geändert, bis du übernimmst.
          </span>
        </div>
        {fehler && <div className="diff-error">❌ {fehler}</div>}

        {bericht && (
          <>
            <div className="diff-summary" style={{ flexWrap: "wrap" }}>
              <span className="diff-info">📄 {bericht.datei} · {bericht.sekunden} s</span>
              {bericht.fehlen?.length > 0 && <span className="diff-info">fehlt in der Datei: {bericht.fehlen.join(", ")}</span>}
              {bericht.ohne_tabelle?.length > 0 && (
                <span className="diff-info" title={bericht.ohne_tabelle.join(", ")}>
                  {bericht.ohne_tabelle.length} Laschen ohne DB-Tabelle (nicht verglichen)</span>
              )}
            </div>
            <div className="diff-tabs" style={{ flexWrap: "wrap" }}>
              {bericht.laschen.map((x, i) => (
                <button key={x.tabelle} className={`diff-tab ${aktiv === i ? "active" : ""}`} onClick={() => setAktiv(i)}>
                  {x.lasche}
                  {anzahl(x) > 0 ? <span className="diff-tab-badge">{x.fehler ? "!" : anzahl(x)}</span> : " ✓"}
                </button>
              ))}
            </div>

            {l && (
              <div className="diff-content">
                <div className="diff-summary">
                  <span className="diff-info">▦ {l.tabelle}</span>
                  {!l.fehler && <>
                    <span className="diff-stat added-stat">+{l.neu.length} neu</span>
                    <span className="diff-stat mod-stat">~{l.geaendert.length} Zeilen geändert ({l.geaendert.reduce((n, g) => n + g.zellen.length, 0)} Zellen)</span>
                    <span className="diff-stat del-stat">{l.nur_in_db.length} nur in der DB</span>
                    <span className="diff-stat ok-stat">={l.unveraendert} gleich{l.verschoben ? ` · ${l.verschoben} nur verschoben` : ""}</span>
                  </>}
                </div>
                {l.fehler && <div className="diff-error">❌ {l.fehler}</div>}
                {!l.fehler && l.neu.length + l.geaendert.length === 0 && <div className="diff-no-changes">✅ Keine Unterschiede</div>}
                {l.fehlend?.length > 0 && (
                  <div className="diff-info" style={{ margin: "6px 0" }}>Spalten nur in der DB (bleiben unverändert): {l.fehlend.join(", ")}</div>
                )}

                {l.geaendert?.length > 0 && (
                  <div className="diff-section">
                    <div className="diff-section-title">
                      Geänderte Zellen
                      <button className="excel-button" style={{ marginLeft: 10 }} onClick={() => alleGeaendert(l, true)}>alle wählen</button>
                      <button className="excel-button" onClick={() => alleGeaendert(l, false)}>keine</button>
                    </div>
                    <div className="diff-table-wrap">
                      <table className="diff-table">
                        <thead><tr><th className="diff-check-col" /><th>Zeile</th><th>Spalte</th><th>in der DB</th><th>in der Excel</th></tr></thead>
                        <tbody>
                          {l.geaendert.map((g) => g.zellen.map((z, j) => {
                            const an = !!s.zellen[g.pk]?.has(z.spalte);
                            return (
                              <tr key={`${g.pk}-${z.spalte}`} className="diff-row-modified">
                                <td className="diff-check-col">
                                  <input type="checkbox" checked={an} onChange={() => zelleUmschalten(l.tabelle, g.pk, z.spalte)} />
                                </td>
                                <td className="diff-pk">{j === 0 ? <>
                                  {g.bezeichnung} <small style={{ opacity: 0.6 }}>Excel-Zeile {g.excel_zeile}</small>
                                  {g.zellen.length > 1 && (
                                    <button className="excel-button" style={{ marginLeft: 6, padding: "0 6px" }}
                                      onClick={() => zeileUmschalten(l.tabelle, g, !(s.zellen[g.pk]?.size === g.zellen.length))}>Zeile</button>
                                  )}
                                </> : ""}</td>
                                <td className="diff-col-name">{z.titel}</td>
                                <td className="diff-old">{fmt(z.db)}</td>
                                <td className="diff-new">{fmt(z.excel)}</td>
                              </tr>
                            );
                          }))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {l.neu?.length > 0 && (
                  <div className="diff-section">
                    <div className="diff-section-title">
                      Neue Zeilen (nur in der Excel)
                      <button className="excel-button" style={{ marginLeft: 10 }} onClick={() => alleNeu(l, true)}>alle wählen</button>
                      <button className="excel-button" onClick={() => alleNeu(l, false)}>keine</button>
                    </div>
                    <div className="diff-table-wrap">
                      <table className="diff-table">
                        <thead><tr><th className="diff-check-col" /><th>Zeile</th><th>Werte</th></tr></thead>
                        <tbody>
                          {l.neu.map((n) => (
                            <tr key={n.excel_zeile} className="diff-row-added">
                              <td className="diff-check-col">
                                <input type="checkbox" checked={s.neu.has(n.excel_zeile)} onChange={() => neuUmschalten(l.tabelle, n.excel_zeile)} />
                              </td>
                              <td className="diff-pk">{n.bezeichnung} <small style={{ opacity: 0.6 }}>Excel-Zeile {n.excel_zeile}</small></td>
                              <td style={{ fontSize: 12 }}>{n.werte.slice(0, 8).map((w) => `${w.titel}: ${fmt(w.wert)}`).join(" · ")}
                                {n.werte.length > 8 ? " …" : ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {l.nur_in_db?.length > 0 && (
                  <div className="diff-section">
                    <div className="diff-section-title">Nur in der Datenbank (bleiben, werden nicht gelöscht)</div>
                    <div style={{ fontSize: 13, padding: "4px 8px" }}>
                      {l.nur_in_db.map((x) => `${x.bezeichnung}${x.quelle ? ` (${x.quelle})` : ""}`).join(" · ")}
                    </div>
                  </div>
                )}

                {l.meldungen?.length > 0 && (
                  <details style={{ margin: "8px 0", fontSize: 12.5 }}>
                    <summary>{l.meldungen.length} Hinweise aus der Excel (z. B. #NV, kein gültiges Datum)</summary>
                    <ul style={{ margin: "6px 0", paddingLeft: 18 }}>{l.meldungen.map((m, i) => <li key={i}>{m}</li>)}</ul>
                  </details>
                )}

                {!l.fehler && (
                  <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 13, marginTop: 6 }}
                    title="Alle neuen und geänderten Zeilen dieser Lasche samt Reihenfolge, Farben und Formaten übernehmen">
                    <input type="checkbox" checked={!!s.ganz} onChange={(e) => setSel(l.tabelle, (x) => ({ ...x, ganz: e.target.checked }))} />
                    ganze Lasche übernehmen (alle Abweichungen + Reihenfolge + Formatierung)
                  </label>
                )}
              </div>
            )}

            {ergebnis && (
              <div className="save-msg ok" style={{ marginTop: 10 }}>
                ✅ Übernommen: {Object.entries(ergebnis).map(([t, e]) => e.fehler ? `${t}: Fehler ${e.fehler}`
                  : `${t}: ${e.ganz ? "ganze Lasche, " : ""}${e.neu || 0} neu, ${e.zellen || 0} Zellen`).join(" · ")}
                {" "}— für einen neuen Vergleich die Datei erneut wählen.
              </div>
            )}

            <div className="diff-modal-footer">
              <span style={{ fontSize: 13, opacity: 0.8 }}>{gewaehlt} ausgewählt</span>
              <button className="diff-apply-btn" disabled={busy || !gewaehlt} onClick={uebernehmen}>
                {busy ? "⏳ übernimmt … (danach Abgleich mit Google, bis ~1 Min.)" : `✓ Ausgewählte in die DB übernehmen (${gewaehlt})`}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function VbaDialog({ B, tabelle, onClose }) {
  const [busy, setBusy] = useState("");
  const [fehler, setFehler] = useState("");
  const laden = async (plattform) => {
    setBusy(plattform); setFehler("");
    try {
      const r = await fetch(`${B}/blatt/${encodeURIComponent(tabelle)}/vba?plattform=${plattform}`);
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `Fehler ${r.status}`);
      const cd = r.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(await r.blob());
      a.download = m ? m[1] : `Imho_${tabelle}_${plattform}.bas`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy("");
  };
  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640, textAlign: "left" }}>
        <h2 style={{ marginTop: 0 }}>⬇ Excel-Modul (VBA) für „{tabelle}“</h2>
        <p style={{ fontSize: 14 }}>
          Das Modul legt vorne in der Mappe die Lasche <b>„IMHO“</b> mit Knöpfen an:
        </p>
        <ul style={{ fontSize: 13.5, lineHeight: 1.5, marginTop: 0 }}>
          <li><b>Daten ziehen</b>: Datenbank → Excel. Fehlt die Lasche, wird sie im Original-Aussehen aufgebaut; sonst werden
            die Werte eingetragen (Formeln bleiben, fehlende Zeilen kommen dazu).</li>
          <li><b>Daten hochladen</b>: Excel → Datenbank, mit Rückfrage. Neue Zeilen landen an derselben Stelle wie in Excel.</li>
          <li><b>Live starten / stoppen</b>: gleicht alle 15 Sekunden in beide Richtungen ab (optional).</li>
        </ul>
        <p style={{ fontSize: 13, opacity: 0.85 }}>
          Gelöscht wird nichts. Dein persönlicher Zugang ist schon eingetragen — die Datei nicht weitergeben.
        </p>
        <div style={{ display: "flex", gap: 10, margin: "10px 0" }}>
          <button className="diff-apply-btn" disabled={!!busy} onClick={() => laden("mac")}>{busy === "mac" ? "⏳ …" : "⬇ für Mac"}</button>
          <button className="diff-apply-btn" disabled={!!busy} onClick={() => laden("windows")}>{busy === "windows" ? "⏳ …" : "⬇ für Windows"}</button>
        </div>
        {fehler && <div className="diff-error">❌ {fehler}</div>}
        <ol style={{ fontSize: 13.5, lineHeight: 1.5 }}>
          <li>Excel-Mappe öffnen (als .xlsm speichern), <b>Extras → Makro → Visual Basic-Editor</b> (Mac) bzw. <b>Alt+F11</b> (Windows).</li>
          <li><b>Datei → Datei importieren …</b> → die heruntergeladene .bas-Datei wählen.</li>
          <li>Einmal das Makro <b>…Einrichten</b> ausführen (Extras → Makro → Makros, oder im Direktfenster eintippen + Return).</li>
          <li>Danach reichen die Knöpfe auf der Lasche „IMHO“. Läuft Live, vor dem Schließen <b>Live stoppen</b>.</li>
        </ol>
        <p style={{ fontSize: 12.5, opacity: 0.8 }}>
          Mac: einmalig die Datei <code>ImhoHttp.scpt</code> nach <code>~/Library/Application Scripts/com.microsoft.Excel/</code> legen
          (siehe backend/excel/README.md). Excel Online führt keine Makros aus — dort läuft der Abgleich nicht.
        </p>
        <div className="popup-buttons"><button className="excel-button" onClick={onClose}>Schließen</button></div>
      </div>
    </div>
  );
}
